import {
  Component,
  input,
  output,
  OnInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RdsButtonModule, RdsFormFieldModule } from '@rds/angular-components';
import { FaroActionDirective } from '../../../directives/faro-action.directive';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from 'rxjs';
import { SinequaService } from '../../../services/sinequa.service';

@Component({
  selector: 'inexp-search-input',
  imports: [
    FormsModule,
    RdsButtonModule,
    RdsFormFieldModule,
    FaroActionDirective,
  ],
  templateUrl: './search-input.component.html',
  styleUrl: './search-input.component.scss',
})
export class SearchInputComponent implements OnInit, OnDestroy {
  // Inputs
  placeholder = input<string>('Enter search query...');
  buttonText = input<string>('Search');
  initialValue = input<string>('');
  showAutocomplete = input<boolean>(true);
  isSearchEnabled = input<boolean>(false);

  // Outputs
  searchSubmit = output<string>();

  // Internal state
  searchQuery = signal<string>('');
  suggestions = signal<string[]>([]);
  showSuggestions = signal<boolean>(false);

  private searchInput$ = new Subject<string>();
  private subscription: Subscription | null = null;

  constructor(private readonly sinequaService: SinequaService) {}

  ngOnInit(): void {
    // Set initial value if provided
    if (this.initialValue()) {
      this.searchQuery.set(this.initialValue());
    }

    // Setup autocomplete with debounce
    if (this.showAutocomplete()) {
      this.subscription = this.searchInput$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((query) => this.sinequaService.autocomplete(query)),
        )
        .subscribe((suggestions) => {
          this.suggestions.set(suggestions);
          this.showSuggestions.set(suggestions.length > 0);
        });
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onSearchInputChange(value: string): void {
    this.searchQuery.set(value);
    if (this.showAutocomplete()) {
      this.searchInput$.next(value);
    }
  }

  onSearchSubmit(): void {
    const query = this.searchQuery().trim();
    this.showSuggestions.set(false);
    this.searchSubmit.emit(query);
  }

  onSuggestionClick(suggestion: string): void {
    this.searchQuery.set(suggestion);
    this.showSuggestions.set(false);
    this.searchSubmit.emit(suggestion);
  }

  onInputFocus(): void {
    if (
      this.showAutocomplete() &&
      this.suggestions().length > 0 &&
      this.searchQuery().trim().length >= 2
    ) {
      this.showSuggestions.set(true);
    }
  }

  onInputBlur(): void {
    // Delay hiding to allow click on suggestion
    setTimeout(() => {
      this.showSuggestions.set(false);
    }, 200);
  }

  highlightMatch(suggestion: string): string {
    const query = this.searchQuery().trim();
    if (!query) return suggestion;

    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    return suggestion.replace(regex, '<strong>$1</strong>');
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
